import JsSIP from 'jssip'

export interface SipAccount {
  /** Endpoint WebSocket seguro do PBX, ex: wss://pbx.exemplo.com:7443 */
  wssUrl: string
  /** Domínio/realm SIP, ex: pbx.exemplo.com */
  domain: string
  /** Ramal/extensão, ex: 1001 */
  extension: string
  password: string
  displayName?: string
  /** Servidor TURN próprio (ex: coturn), ex: turn:turn.exemplo.com:3478 */
  turnUrl?: string
  turnUsername?: string
  turnCredential?: string
}

export type RegState = 'unregistered' | 'connecting' | 'registered' | 'failed'
export type CallState = 'idle' | 'ringing-in' | 'ringing-out' | 'active' | 'held'
/** Estado de um ramal monitorado por BLF. */
export type BlfState = 'unknown' | 'idle' | 'ringing' | 'busy'
/** Estado da perna de consulta na transferência assistida. */
export type ConsultState = 'idle' | 'calling' | 'active'

export interface Voicemail {
  novos: number
  antigos: number
}

export interface IncomingInfo {
  number: string
  name?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySession = any

// STUN é obrigatório: sem ele, o único candidato ICE é .local/privado e a
// chamada cai com 488 Incompatible SDP mesmo com o registro OK.
// Fallback público até existir coturn próprio — trocar quando ele subir.
const PUBLIC_STUN_FALLBACK = [{ urls: 'stun:stun.l.google.com:19302' }]

const buildIceServers = (account: SipAccount): RTCIceServer[] => {
  if (!account.turnUrl) return PUBLIC_STUN_FALLBACK
  return [
    ...PUBLIC_STUN_FALLBACK,
    {
      urls: account.turnUrl,
      username: account.turnUsername,
      credential: account.turnCredential,
    },
  ]
}

// Teto pra coleta de ICE: se nenhum candidato srflx/relay chegar até aqui,
// manda o INVITE com o que tiver em vez de travar esperando para sempre.
const ICE_GATHERING_TIMEOUT_MS = 2500

class SipClient {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private ua: any = null
  private session: AnySession = null
  private iceServers: RTCIceServer[] = PUBLIC_STUN_FALLBACK
  private iceGatherTimer: ReturnType<typeof setTimeout> | null = null
  private audioInputId = ''
  private subs = new Map<string, AnySession>()
  private consultSession: AnySession = null
  private consultTarget = ''
  private consultPending = false

  onRegStateChange: (state: RegState, cause?: string) => void = () => {}
  onCallStateChange: (state: CallState) => void = () => {}
  onIncomingCall: (info: IncomingInfo) => void = () => {}
  onCallEnded: (cause: string) => void = () => {}
  onRemoteStream: (stream: MediaStream | null) => void = () => {}
  onMuteChange: (muted: boolean) => void = () => {}
  onHoldChange: (held: boolean) => void = () => {}
  onTransferResult: (ok: boolean, detail?: string) => void = () => {}
  onVoicemail: (vm: Voicemail) => void = () => {}
  onBlfChange: (extension: string, state: BlfState) => void = () => {}
  onConsultStateChange: (state: ConsultState) => void = () => {}

  /** Microfone a usar nas próximas chamadas ('' = padrão do sistema). */
  setAudioInput(deviceId: string) {
    this.audioInputId = deviceId
  }

  private mediaConstraints() {
    return {
      audio: this.audioInputId ? { deviceId: { exact: this.audioInputId } } : true,
      video: false,
    }
  }

  get isRegistered(): boolean {
    return !!this.ua?.isRegistered?.()
  }

  connect(account: SipAccount) {
    this.disconnect()
    this.iceServers = buildIceServers(account)

    const socket = new JsSIP.WebSocketInterface(account.wssUrl)
    const ua = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${account.extension}@${account.domain}`,
      password: account.password,
      display_name: account.displayName || account.extension,
      register: true,
      session_timers: false,
    })

    ua.on('connecting', () => this.onRegStateChange('connecting'))
    ua.on('disconnected', () => this.onRegStateChange('unregistered'))
    ua.on('registered', () => {
      this.onRegStateChange('registered')
      this.subscribeVoicemail(account)
    })
    ua.on('unregistered', () => this.onRegStateChange('unregistered'))
    ua.on('registrationFailed', (e: { cause?: string }) => this.onRegStateChange('failed', e?.cause))

    ua.on('newRTCSession', (e: { originator: 'local' | 'remote'; session: AnySession; request: AnySession }) => {
      // Segunda sessão local durante uma consulta: é a perna da
      // transferência assistida, não uma chamada nova.
      if (this.consultPending && e.originator === 'local') {
        this.consultPending = false
        this.consultSession = e.session
        this.onConsultStateChange('calling')
        e.session.on('confirmed', () => this.onConsultStateChange('active'))
        e.session.on('accepted', () => this.onConsultStateChange('active'))
        e.session.on('ended', () => this.dropConsult())
        e.session.on('failed', () => this.dropConsult())
        return
      }

      // Só uma chamada por vez: rejeita nova entrada se já houver sessão ativa.
      if (this.session && this.session !== e.session) {
        if (e.originator === 'remote') e.session.terminate({ status_code: 486 })
        return
      }

      this.session = e.session
      this.bindSessionEvents(e.session)

      if (e.originator === 'remote') {
        const from = e.request.from
        this.onCallStateChange('ringing-in')
        this.onIncomingCall({
          number: from?.uri?.user ?? 'Desconhecido',
          name: from?.display_name || undefined,
        })
      } else {
        this.onCallStateChange('ringing-out')
      }
    })

    this.ua = ua
    ua.start()
  }

  disconnect() {
    this.clearSubscriptions()
    if (this.consultSession) {
      try { this.consultSession.terminate() } catch { /* já encerrada */ }
      this.consultSession = null
    }
    if (this.session) {
      try { this.session.terminate() } catch { /* já encerrada */ }
      this.session = null
    }
    if (this.ua) {
      this.ua.stop()
      this.ua = null
    }
  }

  private bindSessionEvents(session: AnySession) {
    // Em ligações de saída, o JsSIP já cria a RTCPeerConnection (e emite
    // 'peerconnection') antes de emitir 'newRTCSession' — quando chegamos
    // aqui esse evento já passou. session.connection expõe a mesma
    // instância direto; 'peerconnection' fica como fallback (é o caminho
    // usado nas ligações de entrada, onde a pc só é criada no answer()).
    const attachTrackListener = (pc: RTCPeerConnection | null | undefined) => {
      if (!pc || (pc as unknown as { _trackListenerAttached?: boolean })._trackListenerAttached) return
      ;(pc as unknown as { _trackListenerAttached?: boolean })._trackListenerAttached = true
      pc.addEventListener('track', (ev: RTCTrackEvent) => {
        // streams[0] pode vir vazio no Chrome (unified plan) — monta a partir da track.
        const stream = ev.streams[0] ?? new MediaStream([ev.track])
        this.onRemoteStream(stream)
      })
    }

    attachTrackListener(session.connection)
    session.on('peerconnection', (data: { peerconnection: RTCPeerConnection }) =>
      attachTrackListener(data.peerconnection))

    session.on('icecandidate', (e: { candidate: RTCIceCandidate; ready: () => void }) => {
      // Candidato srflx/relay (STUN/TURN) já é suficiente pra fechar a
      // chamada na maioria das redes — não vale esperar o resto do
      // gathering (host adicionais, IPv6, etc.) só por completude.
      const usable = e.candidate?.type === 'srflx' || e.candidate?.type === 'relay'
      if (usable) {
        if (this.iceGatherTimer) {
          clearTimeout(this.iceGatherTimer)
          this.iceGatherTimer = null
        }
        e.ready()
        return
      }
      // Rede de segurança: se nenhum srflx/relay vier (STUN bloqueado, por
      // exemplo), não trava pra sempre — manda o que tiver depois do teto.
      // 'ready' já é idempotente do lado do JsSIP (checa 'finished' internamente).
      if (this.iceGatherTimer) return
      this.iceGatherTimer = setTimeout(() => {
        this.iceGatherTimer = null
        e.ready()
      }, ICE_GATHERING_TIMEOUT_MS)
    })

    session.on('accepted', () => this.onCallStateChange('active'))
    session.on('confirmed', () => this.onCallStateChange('active'))

    session.on('ended', (e: { cause?: string }) => this.finishCall(e?.cause ?? 'ended'))
    session.on('failed', (e: { cause?: string }) => this.finishCall(e?.cause ?? 'failed'))
  }

  private finishCall(cause: string) {
    if (this.iceGatherTimer) {
      clearTimeout(this.iceGatherTimer)
      this.iceGatherTimer = null
    }
    this.session = null
    this.onRemoteStream(null)
    this.onCallStateChange('idle')
    this.onCallEnded(cause)
  }

  call(target: string, domain: string) {
    if (!this.ua) throw new Error('UA não conectada')
    const options = {
      mediaConstraints: this.mediaConstraints(),
      pcConfig: { iceServers: this.iceServers },
    }
    this.ua.call(`sip:${target}@${domain}`, options)
  }

  answer() {
    if (!this.session) return
    this.session.answer({
      mediaConstraints: this.mediaConstraints(),
      pcConfig: { iceServers: this.iceServers },
    })
  }

  reject() {
    if (!this.session) return
    this.session.terminate({ status_code: 486 })
  }

  hangup() {
    if (!this.session) return
    this.session.terminate()
  }

  // ── Assinaturas SIP (BLF e MWI) ──────────────────────────────────
  // O FreeSWITCH já publica presença e aviso de recado: manage-presence,
  // auth-subscriptions e send-presence-on-register estão ligados no perfil.
  // Não é preciso mudar nada no PBX — basta assinar.

  private clearSubscriptions() {
    for (const sub of this.subs.values()) {
      try { sub.terminate() } catch { /* já encerrada */ }
    }
    this.subs.clear()
  }

  /** Aviso de recado na caixa postal do próprio ramal. */
  private subscribeVoicemail(account: SipAccount) {
    const key = 'mwi'
    if (this.subs.has(key)) return
    try {
      const sub = this.ua.subscribe(
        `sip:${account.extension}@${account.domain}`,
        'message-summary',
        'application/simple-message-summary',
        { expires: 3600 },
      )
      sub.on('notify', (_final: boolean, _req: unknown, body: string) => {
        // Corpo: "Voice-Message: <novos>/<antigos> (<urgentes>/<...>)"
        const m = /Voice-Message:\s*(\d+)\/(\d+)/i.exec(body || '')
        if (m) this.onVoicemail({ novos: Number(m[1]), antigos: Number(m[2]) })
        else if (/Messages-Waiting:\s*no/i.test(body || ''))
          this.onVoicemail({ novos: 0, antigos: 0 })
      })
      sub.subscribe()
      this.subs.set(key, sub)
    } catch (err) {
      console.warn('[sip] assinatura MWI falhou:', err)
    }
  }

  /**
   * Monitora o estado de outros ramais (BLF). Assina o que faltar e
   * encerra o que saiu da lista — chamar de novo é seguro e barato.
   */
  watchBlf(extensions: string[], domain: string) {
    if (!this.ua) return
    const wanted = new Set(extensions.filter(Boolean))

    for (const [key, sub] of this.subs) {
      if (!key.startsWith('blf:')) continue
      if (wanted.has(key.slice(4))) continue
      try { sub.terminate() } catch { /* já encerrada */ }
      this.subs.delete(key)
    }

    for (const ext of wanted) {
      const key = `blf:${ext}`
      if (this.subs.has(key)) continue
      try {
        const sub = this.ua.subscribe(
          `sip:${ext}@${domain}`,
          'dialog',
          'application/dialog-info+xml',
          { expires: 3600 },
        )
        sub.on('notify', (_final: boolean, _req: unknown, body: string) =>
          this.onBlfChange(ext, parseDialogInfo(body)))
        sub.on('terminated', () => this.onBlfChange(ext, 'unknown'))
        sub.subscribe()
        this.subs.set(key, sub)
      } catch (err) {
        console.warn(`[sip] assinatura BLF de ${ext} falhou:`, err)
      }
    }
  }

  // ── Transferência assistida nativa ───────────────────────────────

  /** Põe a chamada atual em espera e liga para o destino da consulta. */
  startConsult(target: string, domain: string) {
    if (!this.session || this.consultSession) return
    if (!this.session.isOnHold?.().local) this.session.hold()
    this.consultTarget = `sip:${target}@${domain}`
    this.consultPending = true
    this.ua.call(this.consultTarget, {
      mediaConstraints: this.mediaConstraints(),
      pcConfig: { iceServers: this.iceServers },
    })
  }

  /**
   * Conclui a transferência: REFER com Replaces, entregando a chamada
   * original ao destino com quem acabamos de falar. Suportado nativamente
   * pelo JsSIP — ele monta o Refer-To com ?Replaces=call-id;to-tag;from-tag.
   */
  completeConsultTransfer() {
    if (!this.session || !this.consultSession) return
    try {
      const refer = this.session.refer(this.consultTarget, { replaces: this.consultSession })
      refer?.on?.('accepted', () => {
        this.onTransferResult(true)
        this.dropConsult()
        this.finishCall('transferida')
      })
      refer?.on?.('failed', () => this.onTransferResult(false, 'recusada'))
    } catch (err) {
      this.onTransferResult(false, String(err))
    }
  }

  /** Desiste da consulta e retoma a chamada original. */
  cancelConsult() {
    if (this.consultSession) {
      try { this.consultSession.terminate() } catch { /* já encerrada */ }
    }
    this.dropConsult()
    if (this.session?.isOnHold?.().local) this.session.unhold()
  }

  private dropConsult() {
    this.consultSession = null
    this.consultPending = false
    this.consultTarget = ''
    this.onConsultStateChange('idle')
  }

  /**
   * Transferência cega (SIP REFER): entrega a chamada ao destino e sai da
   * linha imediatamente, sem falar antes. O FreeSWITCH anuncia REFER no
   * cabeçalho Allow, então é o caminho nativo.
   */
  transfer(target: string, domain: string) {
    if (!this.session) return
    try {
      const refer = this.session.refer(`sip:${target}@${domain}`)
      refer?.on?.('accepted', () => {
        this.onTransferResult(true)
        this.finishCall('transferida')
      })
      refer?.on?.('failed', (e: { status_line?: { reason_phrase?: string } }) =>
        this.onTransferResult(false, e?.status_line?.reason_phrase || 'recusada'))
    } catch (err) {
      this.onTransferResult(false, String(err))
    }
  }

  /**
   * Transferência assistida aproveitando o att_xfer do FusionPBX: o PBX já
   * escuta *4 no canal (bind_digit_action), pede o destino e conecta as
   * pontas. Evita gerenciar duas sessões SIP simultâneas no cliente.
   */
  attendedTransfer(target: string) {
    if (!this.session) return
    this.sendDtmf('*')
    this.sendDtmf('4')
    // Intervalo para o PBX assumir o canal antes de receber os dígitos.
    setTimeout(() => {
      target.split('').forEach((d, i) => setTimeout(() => this.sendDtmf(d), i * 120))
    }, 600)
  }

  toggleMute(): boolean {
    if (!this.session) return false
    const muted = !!this.session.isMuted?.().audio
    if (muted) this.session.unmute({ audio: true })
    else this.session.mute({ audio: true })
    const next = !muted
    this.onMuteChange(next)
    return next
  }

  toggleHold(): boolean {
    if (!this.session) return false
    const held = !!this.session.isOnHold?.().local
    if (held) this.session.unhold()
    else this.session.hold()
    const next = !held
    this.onCallStateChange(next ? 'held' : 'active')
    this.onHoldChange(next)
    return next
  }

  sendDtmf(digit: string) {
    if (!this.session) return
    try { this.session.sendDTMF(digit) } catch { /* sessão pode não suportar ainda */ }
  }
}

/**
 * Traduz o corpo dialog-info+xml do NOTIFY para um estado simples.
 * Sem <dialog> = ramal livre. early = tocando. confirmed = em chamada.
 */
function parseDialogInfo(body: string): BlfState {
  if (!body) return 'unknown'
  try {
    const doc = new DOMParser().parseFromString(body, 'application/xml')
    const states = Array.from(doc.getElementsByTagName('state'))
      .map((n) => (n.textContent || '').trim().toLowerCase())
      .filter((v) => v && v !== 'full' && v !== 'partial')
    if (states.length === 0) return 'idle'
    if (states.includes('confirmed')) return 'busy'
    if (states.includes('early') || states.includes('proceeding') || states.includes('trying')) return 'ringing'
    return 'idle'
  } catch {
    return 'unknown'
  }
}

export const sipClient = new SipClient()
